import type { CSSProperties } from 'react';

import { lockedFieldTitle } from './LockedFieldset';

const chipRow: CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: 4 };

const chip: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '2px 8px',
  borderRadius: 'var(--radius-xs)',
  border: '1px solid var(--border-subtle)',
  background: 'var(--surface-2)',
  fontFamily: 'var(--font-mono)',
  fontSize: 'var(--fs-xs)',
  color: 'var(--text-primary)',
};

const allowlistNote: CSSProperties = {
  color: 'var(--text-secondary)',
  fontFamily: 'var(--font-sans)',
};

/**
 * The tools of a node whose tool set a Formant template owns (DEV-17471),
 * as a plain list: what the node may call, and nothing that looks like it
 * can be changed here. The editable `ToolRefsField` greyed out still showed
 * a remove glyph on every chip, an "Add MCP connection" select and an "Add
 * platform tool" search, so the lock did not read as one.
 */
export function LockedToolList({
  toolRefs,
  toolAllowlists,
}: {
  toolRefs: string[];
  toolAllowlists: Record<string, string[]>;
}) {
  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}
      data-testid="locked-tool-list"
      title={lockedFieldTitle}
    >
      <span className="field-label">Tools</span>
      {toolRefs.length === 0 ? (
        <p className="t-xs t-muted" style={{ margin: 0 }}>
          This node calls no tools.
        </p>
      ) : (
        <div style={chipRow} role="list" aria-label="Tools (locked)">
          {toolRefs.map((ref) => {
            const allowed = toolAllowlists[ref];
            return (
              <span key={ref} style={chip} role="listitem">
                {ref}
                {allowed && allowed.length > 0 ? (
                  <span style={allowlistNote}>
                    · {allowed.length} tool{allowed.length === 1 ? '' : 's'} allowed
                  </span>
                ) : null}
              </span>
            );
          })}
        </div>
      )}
      <span className="field-help">Locked — the tool set is managed by Formant.</span>
    </div>
  );
}
